import React, { useState, useCallback, useMemo, useRef } from 'react';
import { GoogleMap, Marker } from '@react-google-maps/api';
import { useBridgeState } from '@digitaz/bridge-sdk-client';
import { getMapOptions } from '../../../utils/helpers/getMapOptions';
import useScreenSize from '../../../hooks/useScreenSize';
import { MapContainer, Pin, ShadowDot } from './styled';
import { useGoogleMaps } from '../../../providers/GoogleMapsProvider';

interface MapProps {
  mapOptions?: google.maps.MapOptions;
}

export const Map: React.FC<MapProps> = ({ mapOptions }) => {
  const { mapData, mapRef, setMapRef } = useGoogleMaps();
  const listenerRefs = useRef<google.maps.MapsEventListener[]>([]);
  const { isWebView } = useBridgeState();
  const { isSmall } = useScreenSize();

  const isMarkerDraggable = mapData?.isMarkerDraggable ?? true;
  const [isDragging, setIsDragging] = useState(false);

  const {
    mapOptions: defaultOptions,
    mapStyle,
    mapHeight
  } = useMemo(
    () => getMapOptions(isSmall, isWebView, isMarkerDraggable),
    [isSmall, isWebView, isMarkerDraggable]
  );

  const currentCenter = mapRef?.current ?? mapData?.initialLocation;

  const handleLoad = useCallback(
    (map: google.maps.Map) => {
      if (!isMarkerDraggable) return;

      const dragStartListener = map.addListener('dragstart', () => {
        setIsDragging(true);
      });
      const dragEndListener = map.addListener('dragend', () => {
        setIsDragging(false);
        const mapCenter = map.getCenter();
        if (mapCenter) {
          setMapRef({ lat: mapCenter.lat(), lng: mapCenter.lng() });
        }
      });
      listenerRefs.current = [dragStartListener, dragEndListener];
    },
    [isMarkerDraggable, listenerRefs, setMapRef]
  );

  const handleUnmount = useCallback(() => {
    listenerRefs.current.forEach(listener => {
      listener.remove();
    });
    listenerRefs.current = [];
  }, []);

  const mergedMapOptions = useMemo(
    () => ({
      ...defaultOptions,
      ...mapOptions
    }),
    [defaultOptions, mapOptions]
  );

  if (!currentCenter) return null;

  return (
    <MapContainer $height={mapHeight}>
      <GoogleMap
        zoom={14}
        options={mergedMapOptions}
        center={currentCenter}
        mapContainerStyle={mapStyle}
        onLoad={handleLoad}
        onUnmount={handleUnmount}
      >
        {!isMarkerDraggable && currentCenter && (
          <Marker
            draggable={false}
            position={currentCenter}
            icon="/static/images/new_ad/icons/icon-map-pin.png"
          />
        )}
      </GoogleMap>

      {isMarkerDraggable && currentCenter && (
        <>
          <ShadowDot $dragging={isDragging} />
          <Pin $dragging={isDragging}>
            <img src="/static/images/new_ad/icons/icon-map-pin.png" alt="pin" />
          </Pin>
        </>
      )}
    </MapContainer>
  );
};
